import React, { useMemo, useState } from "react";

const TriggerTag = ({ label, onRemove }) => {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 mr-1 mb-1">
      {label}
      <button
        type="button"
        className="ml-1 flex-shrink-0 text-gray-400 hover:text-gray-500 focus:outline-none"
        onClick={onRemove}
      >
        <svg
          className="h-2 w-2"
          stroke="currentColor"
          fill="none"
          viewBox="0 0 8 8"
        >
          <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
        </svg>
      </button>
    </span>
  );
};

const SectionHeader = ({ title, subtitle, right }) => {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {subtitle ? (
          <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>
        ) : null}
      </div>
      {right}
    </div>
  );
};

const ItemCard = ({
  item,
  index,
  onChange,
  onRemove,
  triggerInputValue,
  onTriggerInputChange,
  onAddTrigger,
  isCollapsed,
  onToggleCollapse,
}) => {
  return (
    <div className="border border-gray-200 rounded-lg bg-white shadow-sm">
      <div className="px-4 py-3 flex items-center justify-between border-b border-gray-100">
        <button
          type="button"
          onClick={onToggleCollapse}
          className="flex items-center gap-2 text-left"
        >
          <svg
            className={`h-4 w-4 text-slate-500 transition-transform ${
              isCollapsed ? "rotate-0" : "rotate-90"
            }`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M6 6a1 1 0 011.707-.707l5 5a1 1 0 01-1.414 1.414l-5-5A1 1 0 016 6z"
              clipRule="evenodd"
            />
          </svg>
          <div className="text-sm font-medium text-slate-700">
            {item.title?.trim() || `Item ${index + 1}`}
          </div>
        </button>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400">
            {(item.triggers || []).length} triggers
          </span>
          <button
            type="button"
            onClick={onRemove}
            className="text-red-600 hover:text-red-800 text-sm font-medium"
          >
            Remove
          </button>
        </div>
      </div>

      {!isCollapsed && (
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Title
            </label>
            <input
              type="text"
              value={item.title || ""}
              onChange={(e) => onChange({ ...item, title: e.target.value })}
              placeholder="Enter title..."
              className="w-full p-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Description
            </label>
            <textarea
              rows={3}
              value={item.description || ""}
              onChange={(e) =>
                onChange({ ...item, description: e.target.value })
              }
              placeholder="Short description..."
              className="w-full p-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              When to use
            </label>
            <textarea
              rows={2}
              value={item.when_to_use || ""}
              onChange={(e) =>
                onChange({ ...item, when_to_use: e.target.value })
              }
              placeholder="Describe when to use this..."
              className="w-full p-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Instructions
            </label>
            <textarea
              rows={6}
              value={item.instructions || ""}
              onChange={(e) =>
                onChange({ ...item, instructions: e.target.value })
              }
              placeholder="Step-by-step guidance..."
              className="w-full p-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700">
              Triggers
            </label>
            <p className="text-xs text-gray-500">
              Keywords. Press Enter to add.
            </p>
            <div className="flex flex-wrap items-start mt-1">
              {(item.triggers || []).map((t) => (
                <TriggerTag
                  key={t}
                  label={t}
                  onRemove={() =>
                    onChange({
                      ...item,
                      triggers: (item.triggers || []).filter((x) => x !== t),
                    })
                  }
                />
              ))}
            </div>
            <input
              type="text"
              value={triggerInputValue}
              onChange={(e) => onTriggerInputChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && triggerInputValue?.trim()) {
                  e.preventDefault();
                  onAddTrigger(triggerInputValue.trim());
                }
              }}
              placeholder="+ Add trigger and press Enter..."
              className="mt-2 w-full p-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const EditAssessmentsInterventions = ({
  assessments = [],
  interventions = [],
  onChange,
}) => {
  // Local input state for the trigger text boxes per item
  const [assessmentTriggerInputs, setAssessmentTriggerInputs] = useState({});
  const [interventionTriggerInputs, setInterventionTriggerInputs] = useState(
    {}
  );
  // Section collapse
  const [assessmentsCollapsed, setAssessmentsCollapsed] = useState(false);
  const [interventionsCollapsed, setInterventionsCollapsed] = useState(false);
  // Per-item expanded state (undefined => expanded by default)
  const [assessmentExpanded, setAssessmentExpanded] = useState({});
  const [interventionExpanded, setInterventionExpanded] = useState({});

  const safeAssessments = useMemo(
    () => (Array.isArray(assessments) ? assessments : []),
    [assessments]
  );
  const safeInterventions = useMemo(
    () => (Array.isArray(interventions) ? interventions : []),
    [interventions]
  );

  const updateAssessments = (next) => onChange("assessments", next);
  const updateInterventions = (next) => onChange("interventions", next);

  const addAssessment = () => {
    updateAssessments([
      ...safeAssessments,
      {
        title: "",
        description: "",
        when_to_use: "",
        instructions: "",
        triggers: [],
      },
    ]);
    setAssessmentExpanded((prev) => ({
      ...prev,
      [safeAssessments.length]: true,
    }));
  };

  const addIntervention = () => {
    updateInterventions([
      ...safeInterventions,
      {
        title: "",
        description: "",
        when_to_use: "",
        instructions: "",
        triggers: [],
      },
    ]);
    setInterventionExpanded((prev) => ({
      ...prev,
      [safeInterventions.length]: true,
    }));
  };

  const changeAssessmentAt = (index, updated) => {
    const next = safeAssessments.map((item, i) =>
      i === index ? updated : item
    );
    updateAssessments(next);
  };

  const changeInterventionAt = (index, updated) => {
    const next = safeInterventions.map((item, i) =>
      i === index ? updated : item
    );
    updateInterventions(next);
  };

  const removeAssessmentAt = (index) => {
    updateAssessments(safeAssessments.filter((_, i) => i !== index));
    setAssessmentExpanded((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const removeInterventionAt = (index) => {
    updateInterventions(safeInterventions.filter((_, i) => i !== index));
    setInterventionExpanded((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const setAssessmentTriggerInput = (index, value) => {
    setAssessmentTriggerInputs((prev) => ({ ...prev, [index]: value }));
  };

  const setInterventionTriggerInput = (index, value) => {
    setInterventionTriggerInputs((prev) => ({ ...prev, [index]: value }));
  };

  const addAssessmentTrigger = (index, trigger) => {
    const current = safeAssessments[index] || {};
    const unique = new Set([...(current.triggers || []), trigger]);
    changeAssessmentAt(index, { ...current, triggers: Array.from(unique) });
    setAssessmentTriggerInput(index, "");
  };

  const addInterventionTrigger = (index, trigger) => {
    const current = safeInterventions[index] || {};
    const unique = new Set([...(current.triggers || []), trigger]);
    changeInterventionAt(index, { ...current, triggers: Array.from(unique) });
    setInterventionTriggerInput(index, "");
  };

  return (
    <div className="mt-12">
      <h2 className="text-xl font-semibold text-slate-700 mb-2">
        Assessments & Interventions
      </h2>
      <p className="text-gray-500 text-sm mb-4">
        Define quick assessments and evidence-based interventions that the AI
        can recommend and guide through, based on conversational triggers.
      </p>
      <div className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white">
        <div className="p-6">
          {/* Assessments */}
          <SectionHeader
            title="Assessments"
            subtitle="Short diagnostics or scales to understand the user's situation."
            right={
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    // Expand all
                    const expanded = {};
                    safeAssessments.forEach((_, i) => {
                      expanded[i] = true;
                    });
                    setAssessmentExpanded(expanded);
                  }}
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200"
                >
                  Expand all
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const collapsed = {};
                    safeAssessments.forEach((_, i) => {
                      collapsed[i] = false;
                    });
                    setAssessmentExpanded(collapsed);
                  }}
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200"
                >
                  Collapse all
                </button>
                <button
                  type="button"
                  onClick={() => setAssessmentsCollapsed((v) => !v)}
                  className="px-3 py-2 bg-gray-50 text-gray-600 rounded-md text-sm hover:bg-gray-100"
                >
                  {assessmentsCollapsed ? "Show" : "Hide"}
                </button>
                <button
                  type="button"
                  onClick={addAssessment}
                  className="inline-flex justify-center rounded-md border border-transparent bg-black px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-800"
                >
                  + Add Assessment
                </button>
              </div>
            }
          />

          {!assessmentsCollapsed && (
            <div className="space-y-4">
              {safeAssessments.length === 0 ? (
                <div className="text-sm text-gray-500 italic">
                  No assessments added yet.
                </div>
              ) : null}
              {safeAssessments.map((a, idx) => (
                <ItemCard
                  key={`assess-${idx}`}
                  item={a}
                  index={idx}
                  onChange={(updated) => changeAssessmentAt(idx, updated)}
                  onRemove={() => removeAssessmentAt(idx)}
                  triggerInputValue={assessmentTriggerInputs[idx] || ""}
                  onTriggerInputChange={(val) =>
                    setAssessmentTriggerInput(idx, val)
                  }
                  onAddTrigger={(val) => addAssessmentTrigger(idx, val)}
                  isCollapsed={assessmentExpanded[idx] === false}
                  onToggleCollapse={() =>
                    setAssessmentExpanded((prev) => ({
                      ...prev,
                      [idx]: prev[idx] === false ? true : false,
                    }))
                  }
                />
              ))}
            </div>
          )}

          {/* Divider */}
          <div className="my-8 h-px bg-gray-200" />

          {/* Interventions */}
          <SectionHeader
            title="Interventions"
            subtitle="Actionable techniques the AI can guide the user through."
            right={
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const expanded = {};
                    safeInterventions.forEach((_, i) => {
                      expanded[i] = true;
                    });
                    setInterventionExpanded(expanded);
                  }}
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200"
                >
                  Expand all
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const collapsed = {};
                    safeInterventions.forEach((_, i) => {
                      collapsed[i] = false;
                    });
                    setInterventionExpanded(collapsed);
                  }}
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200"
                >
                  Collapse all
                </button>
                <button
                  type="button"
                  onClick={() => setInterventionsCollapsed((v) => !v)}
                  className="px-3 py-2 bg-gray-50 text-gray-600 rounded-md text-sm hover:bg-gray-100"
                >
                  {interventionsCollapsed ? "Show" : "Hide"}
                </button>
                <button
                  type="button"
                  onClick={addIntervention}
                  className="inline-flex justify-center rounded-md border border-transparent bg-black px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-800"
                >
                  + Add Intervention
                </button>
              </div>
            }
          />

          {!interventionsCollapsed && (
            <div className="space-y-4">
              {safeInterventions.length === 0 ? (
                <div className="text-sm text-gray-500 italic">
                  No interventions added yet.
                </div>
              ) : null}
              {safeInterventions.map((it, idx) => (
                <ItemCard
                  key={`interv-${idx}`}
                  item={it}
                  index={idx}
                  onChange={(updated) => changeInterventionAt(idx, updated)}
                  onRemove={() => removeInterventionAt(idx)}
                  triggerInputValue={interventionTriggerInputs[idx] || ""}
                  onTriggerInputChange={(val) =>
                    setInterventionTriggerInput(idx, val)
                  }
                  onAddTrigger={(val) => addInterventionTrigger(idx, val)}
                  isCollapsed={interventionExpanded[idx] === false}
                  onToggleCollapse={() =>
                    setInterventionExpanded((prev) => ({
                      ...prev,
                      [idx]: prev[idx] === false ? true : false,
                    }))
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditAssessmentsInterventions;
